import { isJson } from "Libs/utils";
import { apiProxyRequest as request } from "./apiProxyRequest";

export interface Link {
  href: string;
  method?: string;
}

export interface ResourceWithLinks {
  _links: { [key: string]: Link };
}

export type PaginatedResource<T = unknown> = {
  items: Array<T>;
  _links: { next?: { href: string } };
};

export const hasLink = <
  Resource extends ResourceWithLinks,
  Key extends keyof Resource["_links"] & string
>(
  resource: Resource,
  key: Key
) => resource._links[key];

/**
 * Makes sure the resource has the requested hal link before invoking the
 * callback.  This can be specially useful for links that can be present bsaed
 * on the user access
 */
export const resolveIfHasLink = <
  Resource extends ResourceWithLinks,
  Callback extends (...args: any) => any,
  Key extends keyof Resource["_links"] & string
>(
  resource: Resource,
  key: Key,
  resolveFunction: Callback
) => (hasLink(resource, key) ? resolveFunction : undefined);

export type APIError = { status?: string; code?: number; title: string } & (
  | {
      message: string;
      detail?: never;
    }
  | {
      message?: never;
      // Some errors can have extra details for instance, on a post payload it could
      // explain which fields are not valid
      detail: string;
    }
);

function parseUrl(url) {
  const parse_url =
    /^(?:([A-Za-z]+):)?(\/{0,3})([0-9.\-A-Za-z]+)(?::(\d+))?(?:\/([^?#]*))?(?:\?([^#]*))?(?:#(.*))?$/;
  return parse_url.exec(url);
}

export const getBaseUrl = (selfHref: string) => {
  const parsedUrl = parseUrl(selfHref);

  let baseUrl = "";

  if (parsedUrl?.[1] === "http" || parsedUrl?.[1] === "https") {
    baseUrl = `${parsedUrl?.[1]}:${parsedUrl?.[2]}${parsedUrl?.[3]}${
      parsedUrl?.[4] ? `:${parsedUrl?.[4]}` : ""
    }`;
  }

  return baseUrl;
};

const checkFields = (obj: object): obj is APIError =>
  (("message" in obj && typeof obj.message === "string") ||
    ("detail" in obj && typeof obj.detail === "string")) &&
  "title" in obj &&
  typeof obj.title === "string";

export const validateAPIError = (potentialError: unknown) => {
  // In most cases the error thrown by the psh-client is a string that needs
  // parsing so this function works based on that premise.
  if (typeof potentialError === "string" && isJson(potentialError)) {
    const error = JSON.parse(potentialError);
    if (checkFields(error)) {
      return error;
    }
  }

  // This check is relevant to check that the error is defined, otheriwse it could be null
  if (!!potentialError && typeof potentialError === "object")
    if (checkFields(potentialError)) {
      return potentialError;
    }

  return undefined;
};

export const resolveNextPage = <Response extends PaginatedResource>(
  responseOrURL: Response | string
) => {
  const url =
    typeof responseOrURL === "string"
      ? responseOrURL
      : responseOrURL._links.next?.href;

  if (!url) return;

  return request<Response>(url);
};

// Like PaginatedResource, but tolerating responses that type `_links` as
// optional or that carry other links (several billing-system collections do).
type AutoPaginatedResource = {
  items: unknown[];
  _links?: { next?: { href: string }; [link: string]: unknown };
};

/**
 * Follows a paginated collection's `_links.next` until exhausted and resolves
 * to the first page with every page's items concatenated (and no `next` link,
 * since the merged result has no further pages). For consumers that need the
 * complete collection rather than a page at a time — e.g. the new billing
 * system's endpoints, which paginate at 50 items.
 */
export const fetchAllPages = async <Response extends AutoPaginatedResource>(
  firstPage: Promise<Response> | Response
): Promise<Response> => {
  const first = await firstPage;

  let page = first;
  const items = [...first.items];
  while (page._links?.next?.href) {
    page = await request<Response>(page._links.next.href);
    items.push(...page.items);
  }

  if (page === first) return first;

  // Keep the first page's metadata (self link, counts, ...); only the item
  // list spans all pages.
  return { ...first, items, _links: { ...first._links, next: undefined } };
};

/**
 * Encodes parameters only if they are not already encoded
 * @param params - Object containing parameters to encode
 * @returns Object with encoded parameters
 */
export const encodeParameters = (
  params: Record<string, string>
): Record<string, string> => {
  const encodedParams: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      // Check if the value is already encoded by comparing it with its decoded version
      try {
        const decoded = decodeURIComponent(value);
        const isAlreadyEncoded = decoded !== value;

        encodedParams[key] = isAlreadyEncoded
          ? value
          : encodeURIComponent(value);
      } catch {
        // If decodeURIComponent throws, the string is likely malformed or invalid
        // In this case, we'll encode it to be safe
        encodedParams[key] = encodeURIComponent(value);
      }
    } else {
      encodedParams[key] = value;
    }
  }

  return encodedParams;
};

export const pickFromFields = (data: Record<string, any>, fields: string[]) =>
  Object.keys(data)
    .filter(k => fields.includes(k))
    .reduce<Record<string, any>>((acc, k) => {
      acc[k] = data[k];

      return acc;
    }, {});
